<template>
  <section ref="screenRoot" class="page admin-screen" data-test="admin-senders">
    <div class="anim" data-delay="80">
      <Eyebrow>Admin · reminder senders</Eyebrow>
      <DisplayHeading lead="Per-project" accent="from" />
      <p class="display-sub">
        Sets the "Sent on behalf of …" line on Slack reminder DMs. When a Slack
        email is configured, the signoff renders as a clickable mention chip
        in the recipient's DM. Without an override, the signoff falls back to
        whoever clicks the "Send reminder" button.
      </p>
      <AdminTabs />
    </div>

    <div v-if="loadError" class="error-state anim" data-delay="160">
      <p class="error-title">Couldn't load projects</p>
      <p class="error-msg">{{ loadError }}</p>
      <button class="retry" type="button" @click="load()">Retry</button>
    </div>

    <div v-else-if="loading && rows.length === 0" class="skeletons anim" data-delay="160">
      <div v-for="i in 3" :key="i" class="skel-row" />
    </div>

    <div v-else class="senders-table anim" data-delay="160">
      <header class="senders-head">
        <span class="lbl-mono">Project</span>
        <span class="lbl-mono">Display name</span>
        <span class="lbl-mono">Slack email</span>
        <span class="lbl-mono">Resolved</span>
        <span aria-hidden="true" />
      </header>

      <article
        v-for="r in rows"
        :key="r.key"
        class="sender-row"
        :data-test="`sender-row-${r.key}`"
      >
        <div class="cell key-cell">
          <span class="key-chip">{{ r.key }}</span>
        </div>

        <div class="cell">
          <input
            v-model="r.draftName"
            type="text"
            placeholder="e.g. Amine Aitelharraj"
            class="input"
            :data-test="`name-${r.key}`"
          />
        </div>

        <div class="cell">
          <input
            v-model="r.draftEmail"
            type="email"
            placeholder="optional — enables @ tag"
            class="input"
            :data-test="`email-${r.key}`"
          />
        </div>

        <div class="cell resolved">
          <span v-if="r.saved.operator_slack_id" class="slack-chip" :title="r.saved.operator_slack_email">
            &lt;@{{ r.saved.operator_slack_id }}&gt;
          </span>
          <span v-else-if="r.saved.operator_name" class="muted">plain text</span>
          <span v-else class="muted">— not set —</span>
        </div>

        <div class="cell action">
          <button
            type="button"
            class="btn-primary"
            :disabled="!canSave(r) || r.saving"
            :data-test="`save-${r.key}`"
            @click="save(r)"
          >
            {{ r.saving ? "Saving…" : "Save" }}
          </button>
        </div>

        <div v-if="r.warning" class="row-warning">{{ r.warning }}</div>
        <div v-if="r.error" class="row-error">{{ r.error }}</div>
        <div v-if="r.justSaved" class="row-ok">Saved.</div>
      </article>
    </div>

    <div class="footnote anim" data-delay="400">
      <span>{{ rows.length }} project{{ rows.length === 1 ? "" : "s" }}</span>
      <span class="dotsep" />
      <span>Override applies to all sprints under the project key.</span>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import DisplayHeading from "@/components/scorecard/DisplayHeading.vue";
import Eyebrow from "@/components/scorecard/Eyebrow.vue";
import AdminTabs from "@/features/admin/AdminTabs.vue";
import { useReveal } from "@/composables/useReveal";
import { useApi } from "@/composables/useApi";
import { useProjectsStore } from "@/stores/projects";
import {
  fetchProjectSender,
  setProjectSender,
  type ProjectSender,
} from "@/api/admin";
import type { ApiError } from "@/api/types";

interface SenderRow {
  key: string;
  draftName: string;
  draftEmail: string;
  saved: ProjectSender;
  saving: boolean;
  error: string;
  warning: string;
  justSaved: boolean;
}

const screenRoot = ref<HTMLElement | null>(null);
useReveal(screenRoot);

const projects = useProjectsStore();
const { client } = useApi();
const loading = ref(false);
const loadError = ref("");
const rows = reactive<SenderRow[]>([]);

function canSave(r: SenderRow): boolean {
  return r.draftName.trim().length > 0;
}

async function load(): Promise<void> {
  loading.value = true;
  loadError.value = "";
  try {
    await projects.load();
    rows.splice(0, rows.length);
    const keys = projects.items.map((p) => p.key);
    const senders = await Promise.all(
      keys.map((k) => fetchProjectSender(client, k).catch(() => ({
        project_key: k,
        sender: {} as ProjectSender,
      }))),
    );
    for (let i = 0; i < keys.length; i++) {
      const cfg = senders[i].sender ?? {};
      rows.push({
        key: keys[i],
        draftName: cfg.operator_name ?? "",
        draftEmail: cfg.operator_slack_email ?? "",
        saved: cfg,
        saving: false,
        error: "",
        warning: "",
        justSaved: false,
      });
    }
  } catch (err) {
    loadError.value = (err as ApiError).message ?? "Unknown error";
  } finally {
    loading.value = false;
  }
}

async function save(r: SenderRow): Promise<void> {
  r.error = "";
  r.warning = "";
  r.justSaved = false;
  r.saving = true;
  try {
    const resp = await setProjectSender(client, r.key, {
      display_name: r.draftName.trim(),
      slack_email: r.draftEmail.trim() || undefined,
    });
    r.saved = resp.sender;
    if (resp.warning) {
      r.warning = resp.warning;
    } else {
      r.justSaved = true;
      setTimeout(() => { r.justSaved = false; }, 2500);
    }
  } catch (err) {
    r.error = (err as ApiError).message ?? "Save failed";
  } finally {
    r.saving = false;
  }
}

onMounted(load);
</script>

<style scoped>
.senders-table {
  margin-top: 24px;
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
  background: var(--bg-elev);
}
.senders-head,
.sender-row {
  display: grid;
  grid-template-columns: 100px 1fr 1fr 200px 120px;
  gap: 16px;
  align-items: center;
  padding: 12px 16px;
}
.senders-head {
  background: var(--bg-tint);
  border-bottom: 1px solid var(--line);
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-2);
}
.sender-row {
  border-bottom: 1px solid var(--line);
  position: relative;
}
.sender-row:last-child { border-bottom: none; }
.key-chip {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-family: var(--font-mono);
  font-size: 12px;
  background: var(--brand-soft);
  color: var(--brand);
}
.input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  font-size: 13px;
  background: var(--bg);
  color: var(--ink-1);
}
.input:focus {
  outline: none;
  border-color: var(--brand);
}
.cell.resolved { font-family: var(--font-mono); font-size: 12px; }
.slack-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background: #4a154b;
  color: #fff;
  font-size: 11px;
}
.muted { color: var(--ink-3); }
.cell.action { text-align: right; }
.btn-primary {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: var(--brand);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.row-warning, .row-error, .row-ok {
  grid-column: 1 / -1;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  margin-top: 4px;
}
.row-warning { background: #fff7e6; color: #ad6800; border: 1px solid #ffd591; }
.row-error { background: #fff1f0; color: #a8071a; border: 1px solid #ffa39e; }
.row-ok { background: #f6ffed; color: #237804; border: 1px solid #b7eb8f; }
.error-state, .skeletons { margin-top: 24px; }
.error-title { font-weight: 600; }
.error-msg { color: var(--ink-2); }
.retry {
  margin-top: 8px;
  padding: 6px 14px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: var(--bg);
  cursor: pointer;
}
.skel-row { height: 64px; background: var(--bg-tint); border-radius: 8px; margin-bottom: 8px; }
.footnote {
  margin-top: 16px;
  font-size: 12px;
  color: var(--ink-3);
}
.dotsep { margin: 0 8px; }
.dotsep::before { content: "·"; }
</style>
